import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '@/config';
import BaseContainer from '@/components/baseContainer';
import EditBasicForm, { EditBasicFormData, EditBasicFormHandle } from '@/components/form/editBasicForm';
import EditRequestForm, { EditRequestFormData, EditRequestFormHandle } from '@/components/form/editRequestForm';
import { WP_URL } from "@/config";
import { linkToIframe } from "@/utiils";

type UserFromAPI = {
  email: string;
  last_name_kanji: string;
  first_name_kanji: string;
  last_name_alpha: string;
  first_name_alpha: string;
  birthday: string;
  gender: string;
  zip_current: string;
  address_current: string;
  amc?: string;
  phone: string;
  job: string;
  industry?: string;
  industry_other?: string;
  purpose: string;        // CSV: "1,2,99"
  purpose_detail: string;
  purpose_other?: string; // ← その他の自由記入
  mail_opt?:string;
};

async function fetchWithTimeout(input: RequestInfo | URL, init: RequestInit = {}, ms = 15000) {
  const ctrl = new AbortController();
  const id = setTimeout(() => ctrl.abort(), ms);
  try { return await fetch(input, { ...init, signal: ctrl.signal }); }
  finally { clearTimeout(id); }
}

export default function UserEditPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitTried, setSubmitTried] = useState(false);
  const [email, setEmail] = useState('');

  // 子フォーム参照
  const basicRef = useRef<EditBasicFormHandle>(null);
  const reqRef = useRef<EditRequestFormHandle>(null);

  // 初期値
  const [initialBasic, setInitialBasic] = useState<Partial<EditBasicFormData>>();
  const [initialReq, setInitialReq] = useState<Partial<EditRequestFormData> & { purposeCsv?: string }>();

  // バリデーション状態（下部の赤字通知用）
  const [basicOK, setBasicOK] = useState(false);
  const [reqOK, setReqOK] = useState(false);

  useEffect(() => {
    (async () => {
      const token = localStorage.getItem('token');
      if (!token) { navigate('/login'); return; }
      try {
        const res = await fetch(`${API_BASE_URL}/v1/user/info`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error('failed to fetch user');
        const u: UserFromAPI = await res.json();

        setEmail(u.email);

        setInitialBasic({
          last_name_kanji: u.last_name_kanji,
          first_name_kanji: u.first_name_kanji,
          last_name_alphabet: u.last_name_alpha,
          first_name_alphabet: u.first_name_alpha,
          birthday: u.birthday,
          gender: u.gender,
          job: u.job,
          industry: u.industry ?? '',
          industry_other: u.industry_other ?? '',
        });

        setInitialReq({
          zip_current: u.zip_current,
          address_current: u.address_current,
          amc: u.amc ?? '',
          phone: u.phone,
          purpose_detail: u.purpose_detail,
          purposeCsv: u.purpose,          // ← "1,2,99"
          purpose_other: u.purpose_other ?? '',
          mail_opt:u.mail_opt,
        });
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const onBasicValidate = (d: { isValid: boolean; formData: EditBasicFormData | undefined }) => setBasicOK(d.isValid);
  const onReqValidate   = (d: { isValid: boolean; formData: EditRequestFormData | undefined }) => setReqOK(d.isValid);

  const handleSave = async () => {
    if (saving) return;
    setSubmitTried(true);

    const b = await basicRef.current?.validateAndGet();
    const r = await reqRef.current?.validateAndGet();
    if (!b?.ok || !r?.ok || !b.data || !r.data) return;

    const basic = b.data;
    const req   = r.data;

    const payload: Record<string, any> = {
      email,
      last_name_kanji:  basic.last_name_kanji,
      first_name_kanji: basic.first_name_kanji,
      last_name_alpha:  basic.last_name_alphabet,
      first_name_alpha: basic.first_name_alphabet,
      birthday:         basic.birthday,
      gender:           basic.gender,
      zip_current:      req.zip_current,
      address_current:  req.address_current,
      amc:              req.amc ?? '',
      phone:            req.phone,
      job:              basic.job,
      industry:         basic.industry,
      industry_other:   basic.industry_other,
      purpose:          (req.purpose ?? []).join(','), // 配列→CSV
      purpose_detail:   req.purpose_detail,
      // 99が含まれていれば自由記入を送る。含まれなければ空文字。
      purpose_other:    (req.purpose ?? []).includes('99') ? (req.purpose_other ?? '') : '',
      mail_opt:req.mail_opt,
    };

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const res = await fetchWithTimeout(`${API_BASE_URL}/v1/user/info`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      }, 15000);

      if (!res.ok) {
        const msg = await res.text().catch(() => '');
        throw new Error(`update failed ${res.status}: ${msg}`);
      }
      alert('更新完了しました');
      linkToIframe('/user', {isIframeUrl: WP_URL + '/mypage'});
      // navigate('/user/info');
    } catch (e: any) {
      if (e?.name === 'AbortError') alert('タイムアウトしました。ネットワーク状況をご確認ください。');
      else alert('更新に失敗しました');
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-center mt-10">読み込み中...</div>;

  return (
    <div className="py-16">
      <div className="flex flex-col gap-8 pt-8">
        <BaseContainer>
          <div className="xmd:w-[80rem] mx-auto flex flex-col gap-8">
            <p className="m-form-title mb-2">基本情報</p>
            <EditBasicForm
              ref={basicRef}
              enabled={!saving}
              shouldSubmit={false}
              onValidationChange={onBasicValidate}
              initialValues={initialBasic}
            />
          </div>
        </BaseContainer>

        <BaseContainer>
          <div className="xmd:w-[80rem] mx-auto flex flex-col gap-8">
            {/* <p className="m-form-title mb-2">自治体の申請</p> */}
            <EditRequestForm
              ref={reqRef}
              enabled={!saving}
              shouldSubmit={false}
              onValidationChange={onReqValidate}
              initialValues={initialReq}
            />

            {/* ボタン行 */}
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <button
                type="button"
                className="m-btn w-32 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => navigate('/info')}
                disabled={saving}
              >
                キャンセル
              </button>
              <button
                type="button"
                className="m-btn fill w-32 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? '保存中…' : '更新'}
              </button>
            </div>
          </div>
        </BaseContainer>

        {submitTried && (!basicOK || !reqOK) && (
          <p className="text-center text-sm text-red-600">
            入力に不備があります。赤字のエラーをご確認ください。
          </p>
        )}
      </div>
    </div>
  );
}
